import { Checkbox, colors, Tooltip } from '@innovationdepartment/proxima-ui';
import { ContentCopyRounded, RefreshRounded, WarningRounded } from '@mui/icons-material';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import type { ReactNode } from 'react';
import {
  type AdAccountGroup,
  type AdAccountOption,
  adAccountEligibility,
  GROUP_PLATFORM,
  INELIGIBLE_REASONS,
  selectedAdAccountOptions,
  toAdAccountOptions
} from '../lib/adAccountEligibility';
import { LIVE_DELIVERY_PLATFORMS, META_CUSTOM_AUDIENCE_TOS_URL } from '../lib/constants';
import type { TosCheck } from '../store/destinationsSlice';
import type { TargetSelection } from '../store/wizardTypes';
import {
  type AdAccountCapacity,
  type AdAccountsResponse,
  type AdPlatform,
  type DestinationPlatform,
  LOOKALIKE_RATIOS,
  type LookalikeRatio,
  type ValidationError
} from '../types';
import {
  AccountChoice,
  AccountGroup,
  AccountGroupTitle,
  AccountLead,
  AccountMeta,
  AccountRow,
  DefinitionHeading,
  DefinitionPanel,
  DeliverSection,
  IneligibleIcon,
  InlineRow,
  Notice,
  PanelSection,
  PanelSectionHeading,
  PlatformOption,
  PlatformRow,
  RadioDot,
  TosActions,
  TosBadge,
  TosIconButton,
  TosLink,
  TosLinkGroup
} from './form.styles';

const ALL_PLATFORMS: { id: DestinationPlatform; label: string }[] = [
  { id: 'meta', label: 'Meta' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'google', label: 'Google' }
];

/** Platforms offered in the form, in `ALL_PLATFORMS` order. */
const PLATFORMS = ALL_PLATFORMS.filter(platform => LIVE_DELIVERY_PLATFORMS.includes(platform.id));

/* Hidden until the ToS copy is reworked */
const SHOW_UNACCEPTED_TOS_NOTICE = false;

const ACCOUNT_GROUPS: { group: AdAccountGroup; title: string; empty: string | null }[] = [
  {
    group: 'metaCustomer',
    title: "Brand's Meta accounts",
    empty: 'No Meta ad accounts connected for this brand.'
  },
  { group: 'metaProxima', title: 'Proxima-owned Meta accounts', empty: null },
  {
    group: 'tiktokCustomer',
    title: "Brand's TikTok accounts",
    empty: 'No TikTok ad accounts connected for this brand.'
  }
];

const NO_DELIVERED_ACCOUNTS: ReadonlySet<string> = new Set();
const NO_TOS_CHECKS: Record<string, TosCheck> = {};

/* A Proxima-owned account's free custom audience slots */
const slotsLabel = (capacity: AdAccountCapacity): string => {
  if (capacity.remaining === 0) return 'full';
  return `${capacity.remaining} of ${capacity.limit} free`;
};

/* Meta's terms page takes the numeric account id */
const metaTosUrl = (adAccountId: string): string =>
  `${META_CUSTOM_AUDIENCE_TOS_URL}?act=${adAccountId.replace(/^act_/, '')}`;

export type DeliverToProps = {
  targets: TargetSelection;
  adAccounts: AdAccountsResponse | null;
  /** Why the ad accounts could not be loaded; shown instead of "none connected". */
  adAccountsError: string | null;
  /** Ad accounts that already carry a live seed of this audience; none for a draft. */
  deliveredAccountIds?: ReadonlySet<string>;
  /** The latest Meta terms re-check per brand Meta account; see `TosCheck`. */
  tosCheckByAdAccountId?: Record<string, TosCheck>;
  errors: ValidationError[];
  onSelectPlatform: (platform: AdPlatform) => void;
  onToggleAdAccount: (adAccountId: string) => void;
  onToggleRatio: (ratio: LookalikeRatio) => void;
  /** Re-reads one brand Meta account's terms; without it, a failed check offers no Retry. */
  onRecheckTos?: (adAccountId: string) => void;
};

/**
 * The "Deliver to" form: platform, ad accounts, lookalike sizes, then any errors.
 *
 * The detail page drives it for a saved definition and the create wizard drives it for a draft.
 * Only the selected platform's ad accounts are listed.
 * `adAccountEligibility` decides which of them can be picked.
 * The rest are greyed out, with the reason in a tooltip on the icon beside the name.
 * A Proxima-owned account shows its free custom audience slots after its id.
 * The ToS badge sits beside the name; unaccepted Meta terms show no reason icon, since the badge already says so.
 * A brand Meta account that has not accepted Meta's terms gets a link to Meta's terms page beside its badge, with a button that copies the link.
 * A failed terms check shows the failure on the right of the row and offers Retry.
 */
const DeliverTo = ({
  targets,
  adAccounts,
  adAccountsError,
  deliveredAccountIds = NO_DELIVERED_ACCOUNTS,
  tosCheckByAdAccountId = NO_TOS_CHECKS,
  errors,
  onSelectPlatform,
  onToggleAdAccount,
  onToggleRatio,
  onRecheckTos
}: DeliverToProps) => {
  const options = toAdAccountOptions(adAccounts);
  const platformGroups = ACCOUNT_GROUPS.filter(
    ({ group }) => GROUP_PLATFORM[group] === targets.platform
  );
  const pickedAccounts = selectedAdAccountOptions(options, targets.adAccountIds);
  const hasUnacceptedTos =
    SHOW_UNACCEPTED_TOS_NOTICE &&
    targets.platform === 'meta' &&
    options.some(option => option.acceptedTos === false);

  const renderAccount = (option: AdAccountOption) => {
    const eligibility = adAccountEligibility(option, targets.platform, pickedAccounts);
    const checked = targets.adAccountIds.includes(option.adAccountId);
    const reasonIcon =
      eligibility.eligible || eligibility.reason === INELIGIBLE_REASONS.tosNotAccepted
        ? null
        : eligibility.reason;
    return (
      <AccountRow key={option.adAccountId}>
        <AccountLead>
          <AccountChoice dimmed={!eligibility.eligible}>
            <Checkbox
              controlled
              disabled={!eligibility.eligible}
              checked={checked}
              onChange={() => {
                if (eligibility.eligible) onToggleAdAccount(option.adAccountId);
              }}
              label={
                <AccountLabel
                  name={option.name}
                  id={option.adAccountId}
                  slots={option.capacity ? slotsLabel(option.capacity) : null}
                  delivered={deliveredAccountIds.has(option.adAccountId)}
                />
              }
            />
          </AccountChoice>
          {reasonIcon ? (
            <Tooltip title={reasonIcon} placement="top" arrow>
              <IneligibleIcon tabIndex={0} role="img" aria-label={reasonIcon}>
                <WarningRounded fontSize="small" htmlColor={colors.neutral80} />
              </IneligibleIcon>
            </Tooltip>
          ) : null}
          {option.acceptedTos == null ? null : (
            <AccountChoice dimmed={!eligibility.eligible}>
              <TosBadge accepted={option.acceptedTos}>
                {option.acceptedTos ? 'ToS ✓' : 'ToS not accepted'}
              </TosBadge>
            </AccountChoice>
          )}
          {option.acceptedTos === false ? (
            <TosAcceptLink adAccountId={option.adAccountId} name={option.name} />
          ) : null}
        </AccountLead>
        {option.acceptedTos === false ? (
          <TosActions>
            <TosStatus
              adAccountId={option.adAccountId}
              name={option.name}
              check={tosCheckByAdAccountId[option.adAccountId]}
              onRecheck={onRecheckTos}
            />
          </TosActions>
        ) : null}
      </AccountRow>
    );
  };

  return (
    <DeliverToCard>
      <DeliverSection aria-labelledby="deliver-platform-heading">
        <PanelSectionHeading id="deliver-platform-heading">Platform</PanelSectionHeading>
        <PlatformRow role="radiogroup" aria-label="Platform">
          {PLATFORMS.map(platform => {
            const selected = platform.id === targets.platform;
            return (
              <PlatformOption
                key={platform.id}
                type="button"
                role="radio"
                aria-checked={selected}
                selected={selected}
                onClick={() => {
                  if (isAdPlatform(platform.id)) onSelectPlatform(platform.id);
                }}
              >
                <RadioDot selected={selected} />
                {platform.label}
              </PlatformOption>
            );
          })}
        </PlatformRow>
      </DeliverSection>

      <PanelSection aria-labelledby="deliver-accounts-heading">
        <PanelSectionHeading id="deliver-accounts-heading">Ad accounts</PanelSectionHeading>
        {adAccountsError ? (
          <AccountMeta role="alert">Couldn't load ad accounts: {adAccountsError}</AccountMeta>
        ) : null}
        {platformGroups.map(({ group, title, empty }) => {
          const groupOptions = options.filter(option => option.group === group);
          return (
            <AccountGroup key={group}>
              <AccountGroupTitle>{title}</AccountGroupTitle>
              {groupOptions.length === 0 && empty && !adAccountsError ? (
                <AccountRow>
                  <AccountMeta>{empty}</AccountMeta>
                </AccountRow>
              ) : null}
              {groupOptions.map(renderAccount)}
            </AccountGroup>
          );
        })}
        {hasUnacceptedTos ? (
          <Notice tone="warning">
            Terms of Service not accepted for some ad accounts. Pick another or have the brand
            accept ToS first.
          </Notice>
        ) : null}
      </PanelSection>

      <PanelSection aria-labelledby="deliver-lookalikes-heading">
        <PanelSectionHeading id="deliver-lookalikes-heading">Lookalike sizes</PanelSectionHeading>
        <AccountMeta>Optional. Built from the seed on each selected account.</AccountMeta>
        <InlineRow>
          {LOOKALIKE_RATIOS.map(ratio => (
            <Checkbox
              key={ratio}
              controlled
              label={`${ratio}%`}
              checked={targets.lookalikeRatios.includes(ratio)}
              onChange={() => onToggleRatio(ratio)}
            />
          ))}
        </InlineRow>
      </PanelSection>

      {errors.length > 0 ? (
        <Notice tone="error" role="alert">
          <ul>
            {errors.map(error => (
              <li key={`${error.path}-${error.message}`}>
                <code>{error.path}</code>: {error.message}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </DeliverToCard>
  );
};

/** The "Deliver to" card's frame and heading, for the form and for a definition that cannot take it. */
export const DeliverToCard = ({ children }: { children: ReactNode }) => (
  <DefinitionPanel aria-labelledby="audience-targets">
    <DefinitionHeading id="audience-targets">Deliver to</DefinitionHeading>
    {children}
  </DefinitionPanel>
);

const AccountLabel = ({
  name,
  id,
  slots,
  delivered
}: {
  name: string;
  id: string;
  slots: string | null;
  delivered: boolean;
}) => (
  <span>
    {name} <AccountMeta>{id}</AccountMeta>
    {slots ? <AccountMeta> · {slots}</AccountMeta> : null}
    {delivered ? <AccountMeta> · already delivered</AccountMeta> : null}
  </span>
);

/* The Meta terms page link for an unaccepted brand Meta account, with a button that copies it */
const TosAcceptLink = ({ adAccountId, name }: { adAccountId: string; name: string }) => {
  const copyToClipboard = useCopyToClipboard();
  const url = metaTosUrl(adAccountId);
  return (
    <TosLinkGroup>
      <TosLink
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Accept Meta terms for ${name}`}
      >
        Accept ToS
      </TosLink>
      <Tooltip title="Copy link" placement="top" arrow>
        <TosIconButton
          type="button"
          aria-label={`Copy Meta terms link for ${name}`}
          onClick={() => copyToClipboard(url, 'Meta terms link copied')}
        >
          <ContentCopyRounded />
        </TosIconButton>
      </Tooltip>
    </TosLinkGroup>
  );
};

/* The outcome of an unaccepted brand Meta account's last terms check, with Retry when it failed */
const TosStatus = ({
  adAccountId,
  name,
  check,
  onRecheck
}: {
  adAccountId: string;
  name: string;
  check: TosCheck | undefined;
  onRecheck: ((adAccountId: string) => void) | undefined;
}) => (
  <>
    {check?.status === 'error' ? (
      <AccountMeta role="alert">Couldn't check terms: {check.message}</AccountMeta>
    ) : null}
    {check?.status === 'checking' ? <AccountMeta>Checking terms…</AccountMeta> : null}
    {check?.status === 'error' && onRecheck ? (
      <Tooltip title="Retry Meta terms check" placement="top" arrow>
        <TosIconButton
          type="button"
          aria-label={`Retry Meta terms check for ${name}`}
          onClick={() => onRecheck(adAccountId)}
        >
          <RefreshRounded />
        </TosIconButton>
      </Tooltip>
    ) : null}
  </>
);

const isAdPlatform = (id: DestinationPlatform): id is AdPlatform => id !== 'google';

export default DeliverTo;
